import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  TextField,
  Skeleton,
  Chip,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  Tooltip,
  Grid,
  useMediaQuery,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Edit,
  Delete,
  Visibility,
  FilterList,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { axiosAPI } from "../AxiosComponents/Axios";
import { errorNotify } from "../ToastComponent/ToastNotification";
import { encryptId } from "../CryptoComponent";
import CustomPagination from "./CustomPagination";
import CommonButton from "./CommonButton";
import SelectInput from "./SelectInput";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const DynamicTable = ({
  columns = [],
  actions = [],
  title,
  linkvalue,
  dataAPI = "",
}) => {
  const Navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [data, setData] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = parseInt(searchParams.get("page")) || 0;
  const initialRows = parseInt(searchParams.get("rowsPerPage")) || 25;

  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);

  const [filters, setFilters] = useState({});

  const [appliedFilters, setAppliedFilters] = useState({});

  const getData = () => {
    axiosAPI
      ?.get(`${dataAPI}`)
      .then((res) => {
        setLoader(false);

        if (res?.data?.data) {
          setData(res?.data?.data);
        }
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSearchParams({ page: newPage, rowsPerPage: rowsPerPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0); // Reset to page 0 when rows change
    setSearchParams({ page: 0, rowsPerPage: newRows });
  };

  const handleApplyFilters = () => {
    const newParams = {
      page: 0,
      rowsPerPage: rowsPerPage,
      ...filters,
    };

    setAppliedFilters(filters);
    setPage(0);
    setSearchParams(newParams);
  };

  useEffect(() => {
    const initialFilters = {};

    columns.forEach((col) => {
      const value = searchParams.get(col.field);
      if (value !== null) {
        initialFilters[col.field] = value;
      }
    });

    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
  }, [searchParams]);

  const handleResetFilters = () => {
    const resetParams = {
      page: 0,
      rowsPerPage: rowsPerPage,
    };

    setFilters({});
    setAppliedFilters({});
    setPage(0);
    setSearchParams(resetParams); // Clears all filters from URL
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
  }, []);

  const filteredData = data.filter((row) => {
    const columnMatch = columns.every((col) => {
      const field = col.field;
      const rowValue = row[field].toString().trim();
      const filterValueRaw = appliedFilters[field];

      if (filterValueRaw === undefined || filterValueRaw === "") return true;

      const filterValue = filterValueRaw.toString().trim();

      if (field.endsWith("_Status")) {
        return rowValue == filterValue;
      }

      return rowValue.toLowerCase().includes(filterValue.toLowerCase());
    });

    // Additional filter for RPM_Procedure, if filter is applied
    const procedureFilterRaw = appliedFilters["RPM_Procedure"];
    if (procedureFilterRaw && procedureFilterRaw.trim() !== "") {
      const procedureFilter = procedureFilterRaw.trim().toLowerCase();
      const rowProcedure = (row["RPM_Procedure"] || "")
        .toString()
        .trim()
        .toLowerCase();

      return columnMatch && rowProcedure.includes(procedureFilter);
    }

    return columnMatch;
  });
  const isMobile = useMediaQuery("(max-width: 1220px)");
  return (
    <Paper
      sx={{
        maxWidth: isMobile ? "100vw" : `calc(100vw - 300px)`,
        padding: 3,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          {title}
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        {/* Left Side: Search + Reset */}

        <Box sx={{ display: "flex", gap: 1 }}>
          {/* Right Side: Add */}
          <CommonButton
            variant="contained"
            startIcon={<Add />}
            onClick={() => Navigate(linkvalue)}
            background="#E75E1F"
          >
            Add
          </CommonButton>
        </Box>
      </Box>

      {/* Filter Fields */}
      <Box sx={{ maxWidth: "100%", mb: 2 }}>
        <Grid container spacing={2}>
          {columns
            .filter((col) => col.label !== "#")
            .map((col) => {
              const isStatusField = col.field.endsWith("_Status");
              const value = filters[col.field] ?? "";

              return isStatusField ? (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                  >
                    {`Search ${col?.label}`}
                  </Typography>

                  <SelectInput
                    value={value}
                    name="branch"
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        [col.field]: e.target.value,
                      }))
                    }
                    options={[
                      { value: "1", label: "Active" },
                      {
                        value: title == "Salary Pay" ? "2" : "0",
                        label: "Inactive",
                      },
                    ]}
                    placeholder="Select Branch"
                    customStyle={{ width: "100%" }}
                  />
                </Grid>
              ) : (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                  >
                    {col?.label}
                  </Typography>
                  <TextField
                    placeholder={col?.label}
                    variant="outlined"
                    size="small"
                    value={value}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        [col?.field]: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Grid>
              );
            })}
        </Grid>
      </Box>

      <Box
        sx={{
          width: "100%", // Full width
          display: "flex",
          justifyContent: "flex-end", // Align buttons to the right
          alignItems: "flex-end",
          gap: 1,
          my: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Search />}
          onClick={handleApplyFilters}
          sx={{
            textTransform: "capitalize",
            color: "white",
            background: "#006CB8",
          }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestartAlt />}
          onClick={handleResetFilters}
          sx={{ textTransform: "capitalize" }}
        >
          Reset
        </Button>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid transparent",
          borderRadius: "10px",
          maxWidth: "90vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table sx={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const isStatusField = col.field.endsWith("_Id");
                return (
                  <StyledTableCell
                    key={col.field}
                    sx={{
                      textAlign: "left",
                      width: isStatusField ? "80px" : "auto",
                    }}
                  >
                    {col.label}
                  </StyledTableCell>
                );
              })}
              {title != "Recruitment Procedure Mapping" && (
                <StyledTableCell sx={{ textAlign: "center", width: "200px" }}>
                  Actions{" "}
                </StyledTableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loader ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <TableRow key={idx}>
                  {columns.map((_, index) => (
                    <StyledTableCell key={index}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                      />
                    </StyledTableCell>
                  ))}
                  {title != "Recruitment Procedure Mapping" && (
                    <StyledTableCell>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                      />
                    </StyledTableCell>
                  )}
                </TableRow>
              ))
            ) : filteredData && filteredData.length > 0 ? (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <StyledTableRow key={row.id}>
                    {columns.map((col) => (
                      <StyledTableCell key={col.field}>
                        {col.field.endsWith("_Status") ? (
                          <Box sx={{ display: "flex", justifyContent: "left" }}>
                            <Chip
                              label={
                                row[col.field] === 1 ? "Active" : "Inactive"
                              }
                              color={row[col.field] === 1 ? "success" : "error"}
                              variant="outlined"
                              sx={{
                                fontSize: { xs: "10px", sm: "12px" },
                                width: "80px",
                                fontFamily: "Geist, sans-serif",
                                fontWeight: "600",
                              }}
                            />
                          </Box>
                        ) : col.label === "#" ? (
                          (page + 1) * rowsPerPage - (rowsPerPage - (index + 1))
                        ) : col.render ? (
                          col.render(row)
                        ) : (
                          row[col.field]
                        )}
                      </StyledTableCell>
                    ))}

                    {title != "Recruitment Procedure Mapping" && (
                      <StyledTableCell>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          {actions.map(
                            (
                              { icon: Icon, handler, color, tooltip = "" },
                              index
                            ) => (
                              <Tooltip
                                key={index}
                                title={tooltip}
                                PopperProps={{ disablePortal: true }}
                              >
                                <IconButton
                                  color={color}
                                  onClick={() => handler(row)}
                                >
                                  <Icon />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                        </Box>
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={columns.length + 1} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}

      {/* Pagination */}
      {/* <CustomPagination
        component="div"
        count={filteredData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      /> */}

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default DynamicTable;
